import { createContext, type ReactNode, useContext, useMemo } from "react"

import type { QueryBinding } from "./catalog"

type BindingsByPath = Map<string, QueryBinding>

interface BindingsContextValue {
  byPath: BindingsByPath
  /** True while the current dashboard's queryBindings are being re-run. */
  isRefreshing: boolean
  /** Dashboard id, when the bindings come from a persisted dashboard. Used
   *  by per-tile refresh — the API endpoint is keyed on dashboard + binding. */
  dashboardId?: string
  /** True iff the caller is allowed to inspect the SQL behind a binding.
   *  Backend already strips the `sql` field for readers, but we also
   *  hide the popover details + connection metadata behind this flag so
   *  the UI doesn't expose "Bound SQL query — details" affordances that
   *  the reader can't act on. */
  canViewSql: boolean
}

const BindingsContext = createContext<BindingsContextValue | null>(null)

function buildBindingsMap(bindings: QueryBinding[] | undefined): BindingsByPath {
  const map: BindingsByPath = new Map()
  if (!bindings) return map
  for (const b of bindings) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!b?.element_path) continue
    // Key by path alone — a given tile can only have one binding slot
    // (data / rows / value) and we only want one badge per tile.
    map.set(b.element_path, b)
  }
  return map
}

export function BindingsProvider({
  bindings,
  isRefreshing = false,
  dashboardId,
  canViewSql = true,
  children,
}: {
  bindings: QueryBinding[] | undefined
  isRefreshing?: boolean
  dashboardId?: string
  /** Default true preserves backwards compat for chat previews and any
   *  caller that hasn't wired role through yet. */
  canViewSql?: boolean
  children: ReactNode
}) {
  const byPath = useMemo(() => buildBindingsMap(bindings), [bindings])
  const value = useMemo<BindingsContextValue>(
    () => ({ byPath, isRefreshing, dashboardId, canViewSql }),
    [byPath, isRefreshing, dashboardId, canViewSql]
  )
  return <BindingsContext.Provider value={value}>{children}</BindingsContext.Provider>
}

/** Read the dashboard id the bindings belong to, if available. */
export function useBindingsDashboardId(): string | undefined {
  const ctx = useContext(BindingsContext)
  return ctx?.dashboardId
}

/** True iff the caller may inspect the bindings' SQL bodies. */
export function useBindingsCanViewSql(): boolean {
  const ctx = useContext(BindingsContext)
  return ctx?.canViewSql ?? true
}

/**
 * Look up the binding attached to a specific element_path. Returns undefined
 * when no binding exists (i.e. refresh would fall through to AI rebuild).
 */
export function useBindingForPath(elementPath: string | undefined): QueryBinding | undefined {
  const ctx = useContext(BindingsContext)
  if (!ctx || !elementPath) return undefined
  return ctx.byPath.get(elementPath)
}

/**
 * True while the parent {@link BindingsProvider} reports a dashboard
 * refresh in progress. Tiles use this to overlay a skeleton during the
 * gap between the user pressing Refresh and the new spec landing.
 */
export function useIsBindingsRefreshing(): boolean {
  const ctx = useContext(BindingsContext)
  return ctx?.isRefreshing ?? false
}
